import Moment from 'moment';
import type {DurationInputArg2} from 'moment';
import {
    add as dfnAdd,
    addBusinessDays,
    addDays,
    addMonths,
    addQuarters,
    addWeeks,
    addYears,
    differenceInBusinessDays,
    endOfDay as dfnEndOfDay,
    format,
    formatRelative,
    getHours,
    getMinutes,
    getSeconds,
    isAfter as dfnIsAfter,
    isBefore as dfnIsBefore,
    isSameDay,
    isSameMonth,
    isSameWeek,
    isSameYear,
    isToday as dfnIsToday,
    isTomorrow as dfnIsTomorrow,
    isValid,
    isWeekend,
    lastDayOfMonth,
    lastDayOfYear,
    nextMonday,
    parse as dfnParse,
    parseISO,
    parseJSON,
    setHours,
    setMinutes,
    setSeconds,
    startOfDay as dfnStartOfDay,
    startOfMonth as dfnStartOfMonth,
    startOfQuarter,
    startOfTomorrow,
    startOfWeek,
    startOfYear,
} from 'date-fns';

import {fromZonedTime} from 'date-fns-tz';

export type ParseDateResult = {
    dateValue: Date | null,
    objectValue: { hours: number, minutes: number, seconds: number }
};

export type MoveUnit = 'd' | 'w' | 'M' | 'Q' | 'y';

export type TargetUnit = 'day' | 'week' | 'month' | 'year';

/**
 * Retrieves the current date and time for a specified timezone.
 *
 * @param {string} timezone - A string representing the IANA timezone identifier (e.g., 'America/New_York').
 * @returns {Date} A Date object representing the current date and time in the specified timezone.
 */
export function getDateTimeForTimezone(timezone: string): Date {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {timeZone: timezone, timeZoneName: 'short'};
    const dit = now.toLocaleString('en-US', options);

    return new Date(dit.slice(0, 23));
}

/**
 * These are the date formats we will attempt to parse
 */
export const DATE_FORMATS = [
    'YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY', 'DDDD',
    'YYYY-MM-DD Z', 'M/D/YYYY Z', 'MM/DD/YYYY Z',
    Moment.ISO_8601
];

export type DateUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

/**
 * Add [amount] [unit]s of time to the given date.
 *
 * @param {Date} date
 * @param {number} amount
 * @param {DateUnit} unit
 * @returns {Moment.Moment}
 */
export function add(date: Date, amount: number, unit: DateUnit): Date {
    return dfnAdd(date, {
        years: unit === 'year' ? amount : 0,
        months: unit === 'month' ? amount : 0,
        weeks: unit === 'week' ? amount : 0,
        days: unit === 'day' ? amount : 0,
        hours: unit === 'hour' ? amount : 0,
        minutes: unit === 'minute' ? amount : 0,
        seconds: unit === 'second' ? amount : 0,
    });
}

/**
 * Subtract [amount] [unit]s of time to the given date.
 *
 * @param {Date} date
 * @param {number} amount
 * @param {'day'|'week'|'month'|'year'} unit
 * @returns {Moment.Moment}
 */
export function subtract(date: Date, amount: number, unit: DurationInputArg2): Moment.Moment {
    return Moment(date).subtract(amount, unit);
}

/**
 * Set end of business day (5pm).
 * @param {Date} dt
 * @returns {number}
 */
export function set5pm(dt: Date): Date {
    return new Date(dt.setHours(17, 0, 0));
}

/**
 * Returns a date from a date, moment or string.
 * Strings can be interpreted like 'today' or 'tomorrow', or can be literal, like 12/23/2004.
 *
 * @param {Moment|Date|string|number} input
 * @returns {Date}
 */
export function parseDate(input: Moment.Moment | string | number | Date): Date {
    if (input !== 0 && !input) return null; // If null, return null
    else if (typeof (input) === 'number') return new Date(input); // turn numbers into dates
    else if (input instanceof Moment) return (input as Moment.Moment).toDate();  // get the date from the Moment
    else if (input instanceof Date) return input; // just a date

    // Natural language search
    else if (typeof (input) === 'string') {
        let i = 0;
        let dt: Date = null;

        // Try custom formats
        const formats = ['yyyy-MM-dd', 'MM/dd/yyyy', 'M/d/yyyy', 'MM/dd/yy', 'M/d/yy'];

        do {
            dt = dfnParse(input, formats[i++], new Date());
            if (!(dt instanceof Date && isValid(dt))) dt = null;
        }
        while (i < formats.length && !dt);

        if (dt) return dt;

        // Try the standard date parse
        const json = parseJSON(input);
        const iso = parseISO(input);

        if (isValid(json)) return json;
        else if (isValid(iso)) return iso;
        else {
            switch (cleanInput(input)) {
                case 'today':
                    return set5pm(new Date());
                case 'now':
                    return new Date();
                case 'tomorrow':
                    return set5pm(startOfTomorrow());
                case 'next week':
                    return set5pm(nextMonday(new Date()));
                case 'next month':
                    return set5pm(addBusinessDays(lastDayOfMonth(new Date()), 1));
                case 'next year':
                    return set5pm(addBusinessDays(lastDayOfYear(new Date()), 1));
                default:
                    // Nothing worked!
                    return null;
            }
        }
    }

    else throw Error('Could not parse: ' + JSON.stringify(input));
}

/**
 * Date in format MM/DD/YYYY or YYYY-MM-DD, time in HH:MI or HH:MI:SS.
 *
 * @param {string | null | number} input
 * @returns {null}
 */
export function parseDateAndTime(input?: string): Moment.Moment | null {
    if (input === null) return null;

    // TODO: Remove once everything is TypeScript
    if (typeof (input) !== 'string') {
        console.warn(`Can not convert ${JSON.stringify(input)} to datetime`);
        return null;
    }

    // Split date and time components
    const [datePart, timePart] = input.split(' ');
    const date = Moment(parseDate(datePart)); // moment
    const [hh, mi, ss] = timePart.split(':');
    if (hh) date.add(hh, 'hours');
    if (mi) date.add(mi, 'minutes');
    if (ss) date.add(ss, 'seconds');

    return date;
}

/**
 * Parse a datetime from a Date's export.
 * Note: unless you pass milliseconds in, you won't get any.
 *
 * @param {string} sDate
 * @returns {Date|*}
 */
export function parseDateTime(sDate: string): Date | null {
    if (!sDate) return new Date();

    const d = new Date(sDate);
    if (isValid(d)) return d;
    else return null;
}

/**
 * Are the two dates the same (only date portion)
 *
 * @param {Date | string} d1
 * @param {Date | string} d2
 * @param {string} [unit] - if specified, check if these dates are the same given the unit of size
 * @returns {boolean}
 */
export function isSame(d1: Date | string | Moment.Moment, d2: Date | string | Moment.Moment, unit: string = 'd'): boolean {
    if (!d1 && !d2) return false;
    if (typeof d1 === 'string') d1 = parseDate(d1);
    if (typeof d2 === 'string') d2 = parseDate(d2);
    if (Moment.isMoment(d1)) d1 = d1.toDate();
    if (Moment.isMoment(d2)) d2 = d2.toDate();

    switch (unit) {
        case 'd':
        case 'day':
            return isSameDay(d1, d2);
        case 'w':
        case 'week':
            return isSameWeek(d1, d2);
        case 'm':
        case 'month':
            return isSameMonth(d1, d2);
        case 'y':
        case 'year':
            return isSameYear(d1, d2);
        default:
            return isSameDay(d1, d2);
    }
}

/**
 * Is the first date the same or before the second date, from a whole date point of view.
 *
 * @param {Date | string} testDate
 * @param {Date | string} targetDate
 * @returns {boolean}
 */
export function isSameOrBefore(testDate: Date | string, targetDate: Date | string): boolean {
    if (!testDate) return false;
    if (typeof testDate === 'string') testDate = new Date(testDate);
    if (typeof targetDate === 'string') targetDate = new Date(targetDate);

    const sameDay = isSameDay(testDate, targetDate);
    const before = dfnIsBefore(testDate, targetDate);

    return sameDay || before;
}

/**
 * Is the first date the same or after the second date, from a whole date point of view.
 *
 * @param {Date | string} testDate
 * @param {Date | string} targetDate
 * @returns {boolean}
 */
export function isSameOrAfter(testDate: Date | string, targetDate: Date | string): boolean {
    if (!testDate) return false;
    if (typeof testDate === 'string') testDate = new Date(testDate);
    if (typeof targetDate === 'string') targetDate = new Date(targetDate);

    const sameDay = isSameDay(testDate, targetDate);
    const after = dfnIsAfter(testDate, targetDate);

    return sameDay || after;
}


/**
 * Is the given date before (the start of) today from a whole date point of view?
 *
 * @param {string|Date} testDate
 * @param {string|Date} [targetDate]
 * @returns {boolean}
 */
export function isBefore(testDate: string | Date, targetDate: string | Date): boolean {
    if (!testDate) return false;
    if (typeof testDate === 'string') testDate = new Date(testDate);
    if (typeof targetDate === 'string') targetDate = new Date(targetDate);

    if (isSameDay(testDate, targetDate)) return false;
    else return dfnIsBefore(testDate, targetDate);
}

/**
 * Is the first date after the second date, from a whole date point of view.
 *
 * @param {Date | string} testDate
 * @param {Date | string} targetDate
 * @returns {boolean}
 */
export function isAfter(testDate: Date | string, targetDate: Date | string): boolean {
    if (!testDate) return false;
    if (typeof testDate === 'string') testDate = new Date(testDate);
    if (typeof targetDate === 'string') targetDate = new Date(targetDate);

    if (isSameDay(testDate, targetDate)) return false;
    else return dfnIsAfter(testDate, targetDate);
}

/**
 * Take an input as "hh:mm:ss" and return an object with a date and an object ({hours,minutes,seconds})
 *
 * @param {string} input in hh:mm[:ss] am, or hh24:mm[:ss]
 * @returns {ParseDateResult} - milliseconds in duration, or -1 if invalid
 */
export function parseTime(input: string): ParseDateResult {
    // If input is null, return null values in all objects
    if (!input) return {dateValue: null, objectValue: {hours: 0, minutes: 0, seconds: 0}};

    // Determine if we are dealing with civilian or military
    // Result of match is either null, or an array with the matched value, e.g. am
    const match = input.match(/([Aa]|[Pp])[Mm]/g);
    const amPm = match?.[0];
    const isCivilianTime = !!match;

    // If an ampm was found, strip it from the sting and put it into newInput
    const newInput = isCivilianTime && amPm ? input.replace(amPm, '') : input;

    // We should now have a string with either hh:mm:ss or hh:mm.  Need to get the pieces without the colons
    const [h, m, s] = newInput.split(':', 3);

    // Convert strings to numbers
    let hours = Number(h);
    const minutes = Number(m);
    const seconds = s ? Number(s) : 0;

    // If this was civilian time, need to ensure we handle the hours correctly by adding 12 hours to a PM value
    // and converting 12 to 0 if not pm
    if (isCivilianTime && amPm) {
        if (hours === 12 && amPm.toLowerCase() === 'am') hours = 0;
        if (hours < 12 && amPm.toLowerCase() === 'pm') hours = hours + 12;
    }

    // Construct a new date with the values by creating a new Moment with today's date, then set the hh24:mm:ss
    let date = dfnStartOfDay(new Date());
    date = setHours(date, hours);
    date = setMinutes(date, minutes);
    date = setSeconds(date, seconds);

    return {
        dateValue: date,
        objectValue: {hours, minutes, seconds}
    }
}

/**
 * Take an object with hours, minutes and/or seconds and format as a string.
 *
 * @param {object} hhMmSs
 * @param {string|number} hhMmSs.hours
 * @param {string|number} hhMmSs.minutes
 * @param {string|number} hhMmSs.seconds
 * @param {boolean} useMilitaryTime
 * @param {boolean} showSeconds
 */
export function formatTime(hhMmSs: { hours: string | number; minutes: string | number; seconds: string | number; }, useMilitaryTime: boolean = false, showSeconds: boolean = true) {
    if (!hhMmSs) return '';

    // Convert to numbers for consistency
    const hours = Number(hhMmSs.hours);
    const minutes = Number(hhMmSs.minutes);
    const seconds = showSeconds && hhMmSs.seconds ? Number(hhMmSs.seconds) : 0;

    const hrs = useMilitaryTime
                ? hours // For military time, just return the raw hours
                : hours === 0 || hours === 12 // If civilian time, there is no 0, just 12
                  ? 12
                  : hours > 12 // If pm, we use a number <= 12 plus "pm"
                    ? hours - 12
                    : hours;

    // Pad minutes with zeros
    const sm = `${minutes}`.padStart(2, '0');

    // Create the hh:mm result (we have already accounted for military vs civilian time)
    let result = `${hrs}:${sm}`;

    // If we are showing seconds, add them to the end
    if (showSeconds) {
        const ss = `${seconds}`.padStart(2, '0');
        result += `:${ss}`;
    }

    // Finally, add an am or pm for civilian time
    if (!useMilitaryTime) {
        const ampm = hours > 11 ? 'pm' : 'am';
        result += ' ' + ampm;
    }

    return result;
}

/**
 * Take a string representation of a duration in hh:mm:ss.sss and return the duration in milliseconds.
 *
 * @param {string | null} input - duration in hh:mm:ss.sss
 * @returns {number} - duration in milliseconds
 */
export function parseDuration(input: string): number {
    if (input === null || input === undefined) throw new TypeError('Can not parse null or undefined duration');

    if (typeof (input) !== 'string') throw new TypeError('Can not parse a non-string duration');

    let hours = 0
    let minutes: number;
    let seconds: number;

    const hms = input.split(':');
    if (hms.length === 2) {
        minutes = Number.parseInt(hms[0]);
        seconds = Number.parseInt(hms[1]);
    }
    else {
        hours = Number.parseInt(hms[0]);
        minutes = Number.parseInt(hms[1]);
        seconds = Number.parseInt(hms[2]);
    }

    return hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;
}

/**
 * Return the maximum of two dates.  If one of the dates is null, return the other date.
 *
 * @param {Moment.Moment} a
 * @param {Moment.Moment} b
 * @returns {Moment.Moment}
 */
export function max(a: Moment.Moment, b: Moment.Moment): Moment.Moment {
    if (!a && !b) return a;
    else if (!a) return b;
    else if (!b) return a;
    else return Moment.max([a, b]);
}

/**
 * Return the minimum of two dates.  If one of the dates is null, return null.
 *
 * @param {Moment.Moment} a
 * @param {Moment.Moment} b
 * @returns {Moment.Moment}
 */
export function min(a: Moment.Moment, b: Moment.Moment): Moment.Moment {
    if (!a && !b) return a;
    else if (!a) return a;
    else if (!b) return b;
    else return Moment.min([a, b]);
}

/**
 * Format a date using MM/DD/YY (short: false) or MM/DD/YYYY (short: true)
 *
 * @param {Moment.Moment|Date|string} date
 * @param {boolean} short - if true, use 2-digit year, otherwise use 4-digit year
 * @param {boolean} useHyphens - if true, use a hyphenated format with ymd, otherwise slashed with mdy
 * @param {boolean} noYear - do not show year if it is the same as the current year
 * @returns {string | null}
 */
export function formatDate(date: Moment.Moment | Date | string, short: boolean = false, useHyphens: boolean = false, noYear: boolean = false): string | null {
    if (date == null) return null;

    let _date: Date = parseDate(date);
    if (!_date || !isValid(_date)) return null;

    const sameYear = _date.getFullYear() === new Date().getFullYear();

    // Calculate the base (month and day)
    let base = useHyphens ? 'MM-dd' : 'MM/dd';

    // Calculate the year (2, 4 or no digits)
    const length = sameYear && noYear ? 0 : short ? 2 : 4;
    let year = '';
    for (let i = 0; i < length; i++) year += 'y';
    let _format = base;
    if (!noYear) {
        if (useHyphens) _format = `${year}-${base}`;
        else _format = `${base}/${year}`;
    }

    return format(_date, _format);
}

/**
 * Format only the time part of a date
 *
 * @param date
 * @param use24
 * @param showSeconds
 * @returns {string}
 */
// export function formatTime(date, use24 = false, showSeconds = true) {
//     const hours = use24 ? 'HH' : 'h';
//     const seconds = showSeconds ? ':ss' : '';
//     const ampm = use24 ? '' : ' a';
//     const format = `${hours}:mm${seconds}${ampm}`;
//
//     if (date) return Moment(date).local().format(format);
//     else {
//         if (use24 && showSeconds) return '00:00:00';
//         else if (use24 && !showSeconds) return '00:00';
//         else if (!use24 && showSeconds) return '00:00:00 am';
//         else return '00:00 am';
//     }
//
// }

/**
 * Format a date/time using MM/DD/YY HH:MM:SSa (short: false) or MM/DD/YYYY HH:MM:SSa(short: true)
 *
 * @param {Moment.Moment|Date|number} date
 * @param {boolean} [informal] - show date as, e.g. Yesterday
 * @param {boolean} [useHyphens] - use hyphenated date, e.g. 2021-01-04
 * @returns {string | null}
 */
export function formatDateTime(date: Moment.Moment | Date | number, informal: boolean = false, useHyphens: boolean = false): string | null {
    if (!date) return null;

    let _date: Date = null;
    if (isValid(date)) _date = date as Date;
    else if (typeof (date) === 'string') _date = parseDateTime(date);
    else if (typeof (date) === 'number') _date = new Date(date);
    else if (Moment.isMoment(date)) _date = date.toDate();

    if (informal) {
        return formatRelative(_date, new Date());
    }

    const fmt = useHyphens ? 'yyyy-MM-dd hh:mm aaa' : 'MM/dd/yy hh:mm aaa';
    return  format(_date, fmt);
}


/**
 * Return a lowercase-trimmed string
 * @param {string} input
 * @return {string}
 */
function cleanInput(input: string): string {
    return input.toLowerCase().trim();
}

/**
 * Converts a java Date to a Moment.  A null or undefined value returns a null or undefined.
 *
 * @param {Date} date
 * @returns {Moment.Moment | null}
 */
export function toMoment(date: Date | Moment.Moment): Moment.Moment {
    if (date === null) return null;
    if (date === undefined) return undefined;
    if (Moment.isMoment(date)) return date;
    else return Moment(new Date(date));
}

/**
 * Moves the given date to today, while preserving time.
 *
 * @param {Date} [date]
 * @returns {Date}
 */
export function today(date?: Date): Date {
    if (date) {
        let today = new Date();
        today = setHours(today, getHours(date));
        today = setMinutes(today, getMinutes(date));
        today = setSeconds(today, getSeconds(date));
        return today;
    }
    else {
        let today = new Date();
        today = setHours(today, 17);
        today = setMinutes(today, 0);
        today = setSeconds(today, 0);
        return today;
    }
}

/**
 * Moves the given date to tomorrow while preserving time.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function tomorrow(date: Date): Date {
    if (date) return moveNext(date, 'd');
    else {
        let dt = new Date();
        dt = setHours(dt, 17);
        dt = setMinutes(dt, 0);
        dt = setSeconds(dt, 0);
        return dt;
    }
}

/**
 * Moves the date to the first (Mon)day of next week while preserving time.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function nextWeek(date: Date): Date {
    return moveNext(date, 'w');
}

/**
 * Move to first day of the next month while preserving time.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function nextMonth(date: Date): Date {
    return moveNext(date, 'M');
}

/**
 * Move the date out of the weekend, to the next business day.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function moveOutOfWeekend(date: Date): Date {
    let dt = date;
    while (isWeekend(dt)) dt = addDays(dt, 1);
    return dt;
}

/**
 *
 * @param {Date} date - start date
 * @param {MoveUnit} unit - unit of measure, e.g. 'd', 'w', 'M', 'Q', 'y'
 * @returns {Date}
 */
export function moveNext(date: Date, unit: MoveUnit): Date {
    // Moment thinks Sunday is the first day of the week, we want the first WORK day
    if (date) {
        let res: Date;
        const dt = new Date();

        switch (unit) {
            case 'd':
                res = addBusinessDays(dt, 1);
                break;
            case 'w':
                res = startOfWeek(addWeeks(dt, 1));
                break;
            case 'M':
                res = dfnStartOfMonth(addMonths(dt, 1));
                break;
            case 'Q':
                res = startOfQuarter(addQuarters(dt, 1));
                break;
            case 'y':
                res = startOfYear(addYears(dt, 1));
                break;
            default:
                throw new Error(`Did not get a valid unit for moveNext, assuming day: ${unit as string}`);
        }

        res = moveOutOfWeekend(res);
        res = setHours(res, getHours(date));
        res = setMinutes(res, getMinutes(date));
        res = setSeconds(res, getSeconds(date));
        return res;
    }
    else {
        const res = Moment().add(1, unit).startOf(unit).add(1, unit);
        res.set('hours', 17);
        res.set('minutes', 0);
        res.set('seconds', 0);
        return new Date(res.date());
    }
}

/**
 * Return the end of the specified day.
 *
 * @param {Date} date
 * @returns {Date | null}
 */
export function startOfDay(date: Date): Date;
export function startOfDay(date: Date | null | undefined): Date | null;
export function startOfDay(date: Date | null | undefined): Date | null {
    if (date instanceof Moment) console.warn('Moment sent to startOfDay');
    if (date === null || date === undefined) return null;

    else return dfnStartOfDay(date);
}

export function dateAsNumber(date?: Date): number {
    return date ? date.getTime() : 0;
}

/**
 * Return the end of the specified day.
 *
 * @param {Date} date
 * @param {boolean} [businessDay] - use 5pm as an end of day?
 * @returns {Date}
 */
export function endOfDay(date: Date, businessDay?: boolean): Date {
    if (date == null) date = new Date();

    if (businessDay) {
        setHours(date, 17);
        setMinutes(date, 0);
        setSeconds(date, 0);
        return date;
    }
    else return dfnEndOfDay(date);
}

/**
 * Is the given date today?
 *
 * @param {Date} date
 * @returns {boolean}
 */
export function isToday(date: Date): boolean {
    return dfnIsToday(date);
}

/**
 * Is the given date tomorrow?
 *
 * @param {Date} date
 * @returns {boolean}
 */
export function isTomorrow(date: Date): boolean {
    return dfnIsTomorrow(date);
}

/**
 * Compare dates at the LOCAL date level only (ignore time).
 * Returns true if date1's local date is on or before date2's local date.
 *
 * This is useful for comparing task completion dates to invoice end dates where we want
 * to compare the calendar date in the user's timezone, not UTC.
 *
 * For example, a task completed at 7pm on Oct 29 Central time should be included
 * in an invoice with endDate of Oct 29, even though in UTC it's Oct 30.
 *
 * @param date - The date to check
 * @param compareDate - The date to compare against
 * @returns true if date is on or before compareDate (at the local date level)
 */
export function isOnOrBeforeDate(date: Date | null | undefined, compareDate: Date | null | undefined): boolean {
    if (!date || !compareDate) return false;
    // Compare local dates by creating date-only values at midnight local time
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const d2 = new Date(compareDate.getFullYear(), compareDate.getMonth(), compareDate.getDate()).getTime();
    return d1 <= d2;
}

/**
 * Compare dates at the LOCAL date level only (ignore time).
 * Returns true if date1's local date is on or after date2's local date.
 *
 * This is useful for comparing task completion dates to invoice start dates where we want
 * to compare the calendar date in the user's timezone, not UTC.
 *
 * @param date - The date to check
 * @param compareDate - The date to compare against
 * @returns true if date is on or after compareDate (at the local date level)
 */
export function isOnOrAfterDate(date: Date | null | undefined, compareDate: Date | null | undefined): boolean {
    if (!date || !compareDate) return false;
    // Compare local dates by creating date-only values at midnight local time
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const d2 = new Date(compareDate.getFullYear(), compareDate.getMonth(), compareDate.getDate()).getTime();
    return d1 >= d2;
}

/**
 * Is the given date between the start and end dates?  We automatically use the startOfDay and endOfDay for startDate
 * and endDate.
 *
 * @param {Date|number|string} checkDate
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {boolean}
 */
export function isBetween(checkDate: Date | number | string, startDate: Date, endDate: Date): boolean {
    if (!checkDate) return false;
    if ((checkDate instanceof String)) checkDate = new Date(checkDate);

    const check = +checkDate;
    return check >= +startDate && check <= +endDate;
}
// export function isBetween(date: Date | number | string, startDate: Date, endDate: Date): boolean {
//     if (!date) return false;
//     if (!(date instanceof Date)) date = new Date(date);
//
//     const dt = date.getTime();
//     const st = startDate ? dfnStartOfDay(startDate).getTime() : 0;
//     const end = endDate ? dfnEndOfDay(endDate).getTime() : Date.now();
//     return dt >= st && dt <= end;
// }

/**
 * Check to see if a date is in the next week.
 *
 * @param {Date} date
 * @returns {boolean}
 */
export function isNextWeek(date: Date): boolean {
    return isNextUnit(date, 'w');
}

/**
 *
 * @param {Date} date
 * @param {'d'|'w'|'M'|'Q'|'y'} unit
 * @returns {boolean}
 */
export function isNextUnit(date: Date, unit: 'd' | 'w' | 'M' | 'Q' | 'y'): boolean {
    const nextDate = Moment(new Date()).add(1, unit);

    return Moment(date).isBetween(nextDate.startOf(unit).toDate(), nextDate.endOf(unit).toDate());
}

/**
 * @param {Date} date
 * @returns {Date}
 */
export function startOfMonth(date: Date): Date {
    return dfnStartOfMonth(date);
}

/**
 * Get the first day of the unit.
 *
 * @param {Date} date
 * @param {'day'|'week'|'month'|'year'} unit
 * @param {boolean} [last] - start of last unit
 * @returns {Date}
 */
export function startOf(date: Date, unit: 'day' | 'week' | 'month' | 'year', last?: boolean): Date {
    if (last) return Moment(date).startOf(unit).subtract(1, unit).toDate();
    else return Moment(date).startOf(unit).toDate();
}

/**
 * Get the first day of the unit.
 *
 * @param {Date} date
 * @param {'day'|'week'|'month'|'year'} unit
 * @param {boolean} [last] - start of last unit
 * @returns {Date}
 */
export function endOf(date: Date, unit: 'day' | 'week' | 'month' | 'year', last?: boolean): Date {
    if (last) return Moment(date).endOf(unit).subtract(1, unit).toDate();
    else return Moment(date).endOf(unit).toDate();
}

/**
 * Get the number of days in the current month
 * @param {Date} date
 * @returns {number}
 */
export function getDaysInMonth(date: Date): number {
    return Moment(date).daysInMonth();
}

/**
 *
 * @param {Date} date - any date
 * @param {boolean} last - use last year instead of this year?
 * @returns {number}
 */
export function getDaysInYear(date: Date, last: boolean): number {
    const dt = last ? Moment(date).subtract(1, 'year') : Moment(date);

    return dt.isLeapYear() ? 366 : 365;
}

/**
 * Is today a weekday?
 *
 * @param {Date|Moment.Moment} date
 * @returns {boolean}
 */
export function isWeekday(date: Date | Moment.Moment): boolean {
    if (date instanceof Date) return !isWeekend(date);
    else return !isWeekend((date).toDate());
}

/**
 *
 * @param {Date|Moment} start
 * @param {Date|Moment} end
 * @returns {number}
 */
export const getWeekDays = (start: Date | Moment.Moment, end: Date | Moment.Moment): number => {
    const _start = (start instanceof Moment) ? (start as Moment.Moment).toDate() : start as Date;
    const _end = (end instanceof Moment) ? (end as Moment.Moment).toDate() : end as Date;

    const weekdays = differenceInBusinessDays(_end, _start);

    if (!isWeekend(_end)) return weekdays + 1;
    else return weekdays;
}

/**
 * Given the date range and target hours/unit, figure out the target hours per (week)day.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {number} targetHours - target hours per unit
 * @param {'day'|'week'|'month'|'year'} targetUnit
 * @param {number} weekDays - number of weekdays in the date range
 * @returns {number}
 */
export function getDailyTargetHours(startDate: Date, endDate: Date, targetHours: number, targetUnit: TargetUnit, weekDays?: number): number {
    // If we were not passed in weekDays, generate it
    if (!weekDays) weekDays = getWeekDays(startDate, endDate);

    // Now get a conversion
    if (targetUnit === 'day') {
        // Easiest conversion:  target is in days, so we just use the number of weekdays in this range
        return weekDays * targetHours;
    }
    else if (targetUnit === 'week') {
        // Example: 20hrs/week target, 10 days (Sa -> Sa -> M) leaves 6 weekdays, so 20 * 6/5 = 20 * 1.2 = 24 target hours
        return targetHours * weekDays / 5;
    }
    else if (targetUnit === 'month') {
        // Since there are 12 months/year, we can use the week conversion, then divide by 12
        return targetHours * weekDays / 5 / 4;
    }
    else if (targetUnit === 'year') {
        // Since there are 52 weeks per year, we can use the week conversion, then divide by 52
        return targetHours * weekDays / 5 / 52;
    }
    else return 8;
}

/**
 * Convert elapsed time (millis) to a different unit.
 *
 * @param {number} elapsedTime
 * @param {'second'|'minute'|'hour'|'day'} unit
 * @returns {number}
 */
export function convertElapsed(elapsedTime: number, unit: 'second' | 'minute' | 'hour' | 'day'): number {
    const hour = 3600000; // 60m * 60s * 1000ms
    const minute = hour * 60;
    const second = minute * 60;
    const day = hour * 24;

    switch (unit) {
        case 'second':
            return elapsedTime / second;
        case 'minute':
            return elapsedTime / minute;
        case 'hour':
            return elapsedTime / hour;
        case 'day':
            return elapsedTime / day;
        default:
            return elapsedTime;
    }
}

export function convertEasternToUTC(inputDate: string) {
    const easternTimeZone = 'America/New_York';

    // Define the format that matches your input string.
    const dateFormat = "yyyy-MM-dd HH:mm:ss.SSSS";

    // Parse the input date string (the third argument is the reference date)
    const parsedDate = dfnParse(inputDate, dateFormat, new Date());

    // Convert the parsed date from Eastern Time to UTC.
    // zonedTimeToUtc interprets the parsed date as being in the specified time zone.
    return fromZonedTime(parsedDate, easternTimeZone);
}

export function toLocalDateString(d: Date | null | undefined): string | null {
    if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * The goal is to get the date part as a string, then return a date set to the given date, but with T11:59:59
 * added to ensure that it always retains the date part.
 *
 * @param {Date|Moment.Moment|string|number} d
 * @returns {null|Date|Date[]}
 * @private
 */
export function dateOnly(d: Date | Moment.Moment | string | number): Date | Date[] | null {
    if (!d) return null;

    // OWA-571: the surrounding try/catch was unreachable — none of the branches
    // below throw for the declared input types (new Date() and date-fns
    // setHours/etc. return Invalid Date rather than throwing), and an invalid
    // result is already handled by the isValid() guard. Removed per the
    // "no unreachable defensive code" rule (don't v8-ignore).
    let result: Date;
    if (typeof (d) === 'string') {
        result = new Date(d.slice(0, 10) + 'T11:59:59');
    }
    else if (typeof (d) === 'number') {
        result = new Date(d);
    }
    else {
        // OWA-300: Dates in invoice not sticking
        // Note: setHours/setMinutes/setSeconds return NEW Date objects, they don't mutate!
        result = setHours(d as Date, 23);
        result = setMinutes(result, 59);
        result = setSeconds(result, 59);
    }

    if (!isValid(result)) {
        console.warn('dateOnly received unparseable value', typeof (d), d);
        return null;
    }
    return result;
}

/**
 * Take a single or array of dates and cut off the time portion.
 *
 * @param {Date|Date[]|string|string[]|number|number[]} date - Either a single value or an array of values to truncate
 * @returns {Date|Date[]}
 */
export function datesOnly(date: Date | Date[] | string | string[] | number | number[]): Date | Date[] | null {
    if (!date) return null;

    if (Array.isArray(date)) {
        return date.map((d: number | string | Date) => {
            return dateOnly(d) as Date;
        });
    }
    else return dateOnly(date);
}

/**
 * Return only Dates from timestamp values
 * @param {Date|string|number|null} t
 * @returns {Date|null}
 */
export function timestampOnly(t: Date | string | number | null): Date | null {
    if (!t) return null;
    if (t instanceof Date) return t;
    else return new Date(t);
}

/**
 * Take a single or array of timestamps
 *
 * @param {string|string[]|Date|Date[]|number|number[]} timestamp - Either a single value or an array of values to cast to Date
 * @returns {Date|Date[] | null}
 */
export function timestampsOnly(timestamp: string | string[] | Date | Date[] | number | number[]): Date | Date[] | null {
    if (!timestamp) return null;

    if (Array.isArray(timestamp)) {
        return timestamp.map((t: number | string | Date) => {
            return timestampOnly(t) as Date;
        });
    }
    else return timestampOnly(timestamp);
}
